export type ProductDetailMeta = {
  energyBadgeText?: string;
  installmentText?: string;
  whyThisProductTitle?: string;
  whyThisProductText?: string;
  featuresText?: string;
  spiritualBenefitsText?: string;
  careText?: string;
  stonePowerTitle?: string;
  stonePowerText?: string;
  stonePowerTagsText?: string;
  reviewsTitle?: string;
  reviewsSubtitle?: string;
  reviewsText?: string;
  recommendedTitle?: string;
  galleryUrlsText?: string;
};

const META_TAG_PREFIX = "__detail_meta:";

function toBinary(input: string) {
  const bytes = new TextEncoder().encode(input);
  let out = "";
  for (const b of bytes) out += String.fromCharCode(b);
  return out;
}

function fromBinary(input: string) {
  const bytes = Uint8Array.from(input, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function encodeMeta(meta: ProductDetailMeta) {
  const json = JSON.stringify(meta);
  const binary = toBinary(json);
  return btoa(binary);
}

function decodeMeta(encoded: string): ProductDetailMeta | null {
  try {
    const binary = atob(encoded);
    const json = fromBinary(binary);
    const parsed = JSON.parse(json) as ProductDetailMeta;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function emptyProductDetailMeta(): ProductDetailMeta {
  return {
    energyBadgeText: "",
    installmentText: "",
    whyThisProductTitle: "",
    whyThisProductText: "",
    featuresText: "",
    spiritualBenefitsText: "",
    careText: "",
    stonePowerTitle: "",
    stonePowerText: "",
    stonePowerTagsText: "",
    reviewsTitle: "",
    reviewsSubtitle: "",
    reviewsText: "",
    recommendedTitle: "",
    galleryUrlsText: "",
  };
}

function normalizeMeta(meta: ProductDetailMeta | undefined): ProductDetailMeta {
  const src = meta ?? {};
  return {
    energyBadgeText: String(src.energyBadgeText ?? "").trim(),
    installmentText: String(src.installmentText ?? "").trim(),
    whyThisProductTitle: String(src.whyThisProductTitle ?? "").trim(),
    whyThisProductText: String(src.whyThisProductText ?? "").trim(),
    featuresText: String(src.featuresText ?? "").trim(),
    spiritualBenefitsText: String(src.spiritualBenefitsText ?? "").trim(),
    careText: String(src.careText ?? "").trim(),
    stonePowerTitle: String(src.stonePowerTitle ?? "").trim(),
    stonePowerText: String(src.stonePowerText ?? "").trim(),
    stonePowerTagsText: String(src.stonePowerTagsText ?? "").trim(),
    reviewsTitle: String(src.reviewsTitle ?? "").trim(),
    reviewsSubtitle: String(src.reviewsSubtitle ?? "").trim(),
    reviewsText: String(src.reviewsText ?? "").trim(),
    recommendedTitle: String(src.recommendedTitle ?? "").trim(),
    galleryUrlsText: String(src.galleryUrlsText ?? "").trim(),
  };
}

export function hasProductDetailMeta(meta: ProductDetailMeta | undefined) {
  const m = normalizeMeta(meta);
  return Object.values(m).some(Boolean);
}

export function splitProductTagsAndMeta(tags: string[] | undefined) {
  const rows = Array.isArray(tags) ? tags : [];
  const cleanTags: string[] = [];

  let meta = emptyProductDetailMeta();

  for (const raw of rows) {
    const tag = String(raw ?? "").trim();
    if (!tag) continue;

    if (!tag.startsWith(META_TAG_PREFIX)) {
      cleanTags.push(tag);
      continue;
    }

    const encoded = tag.slice(META_TAG_PREFIX.length).trim();
    if (!encoded) continue;

    const decoded = decodeMeta(encoded);
    if (decoded) {
      meta = normalizeMeta(decoded);
    }
  }

  return { cleanTags, meta };
}

export function buildProductTagsWithMeta(tags: string[], meta: ProductDetailMeta | undefined) {
  const cleanTags = Array.from(new Set((tags ?? []).map((t) => String(t ?? "").trim()).filter(Boolean))).filter(
    (tag) => !tag.startsWith(META_TAG_PREFIX),
  );

  if (!hasProductDetailMeta(meta)) return cleanTags;

  const encoded = encodeMeta(normalizeMeta(meta));
  return [...cleanTags, `${META_TAG_PREFIX}${encoded}`];
}

export function parseLines(value: string | undefined) {
  return String(value ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
